/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/browser/KavenBrowserRSA.ts
 * @create:      2020-07-12 22:36:25.199
 * @modify:      2025-10-14 22:39:26.166
 * @version:     6.1.0
 * @times:       32
 * @lines:       214
 * @copyright:   Copyright © 2020-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { String2ArrayBuffer } from "../core/String2ArrayBuffer";
import { ArrayBuffer2String } from "../core/String2ArrayBuffer";
import { ToUint8Array } from "../number/ToUint8Array";
import { ExportCryptoPrivateKey, ExportCryptoPublicKey, ImportCryptoPrivateKey, ImportCryptoPublicKey } from "./KavenBrowserCrypto";

export type TDataOfKavenBrowserRSA = BufferSource;

/**
 * Web Crypto API Wrap
 * @since 2.0.9
 * @version 2020-07-13
 */
export class KavenBrowserRSA {
    private keyForEncrypt: CryptoKey | undefined;
    private keyForDecrypt: CryptoKey | undefined;
    private keyForSign: CryptoKey | undefined;
    private keyForVerify: CryptoKey | undefined;

    public readonly PublicKey: string | undefined;
    public readonly PrivateKey: string | undefined;

    public constructor(publicKey?: string, privateKey?: string) {
        this.PublicKey = publicKey;
        this.PrivateKey = privateKey;
    }

    // #region Core Methods
    public async Encrypt(data: TDataOfKavenBrowserRSA) {
        if (this.PublicKey === undefined) {
            throw new Error("publicKey is undefined");
        }

        if (this.keyForEncrypt === undefined) {
            this.keyForEncrypt = await ImportCryptoPublicKey(this.PublicKey, true);
        }

        return crypto.subtle.encrypt(
            {
                name: "RSA-OAEP",
            },
            this.keyForEncrypt,
            data,
        );
    }

    public async Decrypt(data: TDataOfKavenBrowserRSA) {
        if (this.PrivateKey === undefined) {
            throw new Error("privateKey is undefined");
        }

        if (this.keyForDecrypt === undefined) {
            this.keyForDecrypt = await ImportCryptoPrivateKey(this.PrivateKey, false);
        }

        return crypto.subtle.decrypt(
            {
                name: "RSA-OAEP",
            },
            this.keyForDecrypt,
            data,
        );
    }

    public async Sign(data: TDataOfKavenBrowserRSA, saltLength?: number) {
        if (this.PrivateKey === undefined) {
            throw new Error("privateKey is undefined");
        }

        if (this.keyForSign === undefined) {
            this.keyForSign = await ImportCryptoPrivateKey(this.PrivateKey, true);
        }

        if (saltLength === undefined) {
            // sets the salt length to the digest size
            saltLength = 32;

            // sets it to the maximum permissible value
            // saltLength = CalculateMaxSaltLength((this.keyForSign.algorithm as any).modulusLength, 32);
        }

        return crypto.subtle.sign(
            {
                name: "RSA-PSS",
                saltLength,
            },
            this.keyForSign,
            data,
        );
    }

    public async Verify(data: TDataOfKavenBrowserRSA, signature: TDataOfKavenBrowserRSA, saltLength?: number) {
        if (this.PublicKey === undefined) {
            throw new Error("publicKey is undefined");
        }

        if (this.keyForVerify === undefined) {
            this.keyForVerify = await ImportCryptoPublicKey(this.PublicKey, false);
        }

        if (saltLength === undefined) {
            if (saltLength === undefined) {
                // sets the salt length to the digest size
                saltLength = 32;

                // sets it to the maximum permissible value
                // saltLength = CalculateMaxSaltLength((this.keyForSign.algorithm as any).modulusLength, 32);
            }
        }

        return crypto.subtle.verify(
            {
                name: "RSA-PSS",
                saltLength,
            },
            this.keyForVerify,
            signature,
            data,
        );
    }
    // #endregion

    // #region Helper Methods
    public async EncryptToString<T>(data: T) {
        const dataBuffer = new TextEncoder().encode(JSON.stringify(data));

        // creates a base-64 encoded ASCII string from a "string" of binary data
        // ("btoa" should be read as "binary to ASCII")
        const base64 = btoa(ArrayBuffer2String(await this.Encrypt(dataBuffer)));

        return base64;
    }

    public async DecryptFromString<T = unknown>(data: string) {
        // decodes a base64 encoded string
        // ("atob" should be read as "ASCII to binary").
        const dataBuffer = String2ArrayBuffer(atob(data));

        const decryptedData = await this.Decrypt(dataBuffer);

        const result = JSON.parse(new TextDecoder().decode(decryptedData));

        return result as T;
    }

    public async SignToString<T>(data: T, saltLength?: number) {
        const dataBuffer = new TextEncoder().encode(JSON.stringify(data));
        const signature = await this.Sign(dataBuffer, saltLength);
        const base64 = btoa(ArrayBuffer2String(signature));

        return base64;
    }

    public async VerifyFromString<T>(data: T, signature: string, saltLength?: number) {
        const result = await this.Verify(
            new TextEncoder().encode(JSON.stringify(data)),
            String2ArrayBuffer(atob(signature)),
            saltLength,
        );

        return result;
    }
    // #endregion

    public static async Create(bits = 4096, exponent = 65537) {
        const keyPair = await crypto.subtle.generateKey(
            {
                name: "RSA-OAEP",
                modulusLength: bits,
                publicExponent: ToUint8Array(exponent), // new Uint8Array([1, 0, 1]),
                hash: "SHA-256",
            },
            true,
            ["encrypt", "decrypt"],
        );

        const publicKey = await ExportCryptoPublicKey(keyPair.publicKey!);
        const privateKey = await ExportCryptoPrivateKey(keyPair.privateKey!);

        return new KavenBrowserRSA(publicKey, privateKey);
    }
}
